import {TimelineSprite} from "@nolimitcity/slot-launcher/bin/animation/TimelineSprite";
import {Mental} from "../Mental";
import {TimelineLite, TweenLite} from "gsap"
import {MentalHelper} from "../utils/MentalHelper";
import {MentalGameMode} from "../server/MentalGameMode";
import {SoundManager} from "../sound/SoundManager";
import {MentalSoundConfig} from "../MentalSoundConfig";

export class MentalFireFrame extends PIXI.Container {
    private _animItems: SingleFireFrame[][] = [];
    private _ffCounter1Items: PIXI.Sprite[] = [];
    private _enhancerMeterCountup: PIXI.BitmapText[] = [];
    private _fireFrameCounter: number = 0;
    private _e1LighteningPlayed: boolean = false;
    private _e2LighteningPlayed: boolean = false;
    private _fsExited: boolean = false;
    private _enhancerFireFrameLoop: TimelineSprite[] = [];

    constructor() {
        super();
        this.initAnimation();
    }

    private _chainSoundPlayed: boolean = false;
    public set chainSoundPlayed(value: boolean) {
        this._chainSoundPlayed = value;
    }

    public showEnhancerMeter(): void {
        // #149
        this._ffCounter1Items.forEach((value: PIXI.Sprite) => value.visible = true);

        this._enhancerMeterCountup.forEach((value: PIXI.BitmapText) => value.visible = true);
    }

    public hideEnhancerMeter(isFirstOpen: boolean, isSecondOpen: boolean): void {
        this._enhancerMeterCountup[0].visible = !isFirstOpen;
        this._enhancerMeterCountup[1].visible = !isSecondOpen;
    }

    public playBoxOpenAnim(isFirstOpen: boolean, isSecondOpen: boolean): void {
        const tl: TimelineLite = new TimelineLite();
        tl.add(this.removeFireFrame());

        if (isFirstOpen) {
            // #203, only playing the sound once, no need to play on 2nd enhancer, since that will only be opened if 1st is opened.
            tl.add(() => SoundManager.playSoundEffect(MentalSoundConfig.ENHANCER_HATCHES_FALL), 0);

            tl.add(this.getBoxOpenAnim(0), 0.01);
        }

        if (isSecondOpen) {
            tl.add(this.getBoxOpenAnim(1), 0.01);
        }
    }

    public showLockedFireFrames(isRestore: boolean): void {
        const stickyData: boolean[][] = isRestore ? Mental.model.initData.stickyFires : Mental.model.gameData.stickyFiresPrev;

        for (let i: number = 0; i < stickyData.length; i++) {
            for (let j: number = 0; j < stickyData[i].length; j++) {
                if (stickyData[i][j]) {
                    this._animItems[i][j].playFFLockAnimation(isRestore);

                    this._fireFrameCounter++;

                    // #334, #332
                    if (!this._chainSoundPlayed && !isRestore) {
                        SoundManager.playSoundEffect(MentalSoundConfig.CHAINS[(MentalHelper.getRandomIntInRange(0, 2))]);
                        this._chainSoundPlayed = true;
                    }
                }
            }
        }
        if (isRestore) {
            this.updateEnhancerMeters();
        }
    }

    /**
     * lighting up the fire frame sequentially
     */
    public showFireFrame(fireFrameOnReel: boolean[], reelId: number): TimelineLite {
        const tl: TimelineLite = new TimelineLite();
        let ffSoundPlayed: boolean = false,
            timeAdded: boolean = false;
        tl.add(() => {
            for (let symbolId = 0; symbolId < fireFrameOnReel.length; symbolId++) {
                if (fireFrameOnReel[symbolId]) {
                    const mode: string = Mental.model.gameData.mode;

                    if (mode === MentalGameMode.NORMAL) {
                        this._animItems[reelId][symbolId].startAnimation();
                        this._fireFrameCounter++;
                        Mental.xWaysCounter._xWaysCounterValue[reelId]++;

                        // #137
                        if (!ffSoundPlayed) {
                            SoundManager.playSoundEffect(MentalSoundConfig.REEL_FIRE[reelId]);
                            ffSoundPlayed = true;
                        }
                    } else {
                        this._animItems[reelId][symbolId].playFFLandingLockAnimationInFS();
                        if (!this._animItems[reelId][symbolId].ffLockAnimation.visible) {
                            this._fireFrameCounter++;
                            Mental.xWaysCounter._xWaysCounterValue[reelId]++;

                            // #137
                            if (!ffSoundPlayed) {
                                SoundManager.playSoundEffect(MentalSoundConfig.REEL_FIRE[reelId]);
                                ffSoundPlayed = true;
                            }
                        }
                    }
                    Mental.xWaysCounter.updateXwaysCounter(); // updating XWays counter from here

                    this.updateEnhancerMeters();
                    // #108
                    if (!this._e1LighteningPlayed && this._fireFrameCounter === 4) {
                        this.playLightningAnim(this._animItems[reelId][symbolId].position.x, this._animItems[reelId][symbolId].position.y, true);

                        this._e1LighteningPlayed = (mode !== MentalGameMode.NORMAL);
                    }
                    // #108
                    if (!this._e2LighteningPlayed && this._fireFrameCounter === 6) {
                        this.playLightningAnim(this._animItems[reelId][symbolId].position.x, this._animItems[reelId][symbolId].position.y, false);

                        this._e2LighteningPlayed = (mode !== MentalGameMode.NORMAL);
                    }
                }
            }
        }, 0);

        for (let symbolId = 0; symbolId < fireFrameOnReel.length; symbolId++) {
            if (!timeAdded && fireFrameOnReel[symbolId] && !this._animItems[reelId][symbolId].ffLockAnimation.visible) {
                tl.to(this, {duration: 0.3});
                timeAdded = true;
            }
        }

        return tl;
    }

    public hideFireFrame(reelId: number = -1) {
        if (reelId < 0) {
            this._animItems.forEach(value => value.forEach(value1 => {
                value1.stopAnimation();
            }));
        } else {
            this._animItems[reelId].forEach(value => {
                value.stopAnimation();
            });
        }
    }

    public resetEnhancerMeters(): void {
        // #332
        this._chainSoundPlayed = false;

        this._fireFrameCounter = 0;

        this._enhancerFireFrameLoop[0].stopLoop();
        this._enhancerFireFrameLoop[0].hide();
        this._enhancerFireFrameLoop[0].alpha = 1;

        this._enhancerFireFrameLoop[1].stopLoop();
        this._enhancerFireFrameLoop[1].hide();
        this._enhancerFireFrameLoop[1].alpha = 1;


        this._enhancerMeterCountup.forEach((value: PIXI.BitmapText, index: number) => {
            // #107
            if (Mental.model.data.mode === MentalGameMode.NORMAL || this._fsExited) {
                // #149
                if (value.visible) {
                    this._ffCounter1Items[index].visible = true;
                }

                value.text = ((index === 0) ? " /4" : " /6");
            }
        });

        this._fsExited = false;
    }

    // #107
    public exitFS(): void {
        this._e1LighteningPlayed = false;
        this._e2LighteningPlayed = false;

        this._fsExited = true;
    }

    private initAnimation(): void {
        const visibleSymbols: number[] = [3, 2, 3, 2, 3];
        for (let i: number = 0; i < visibleSymbols.length; i++) {
            this._animItems[i] = [];
            for (let j = 0; j < visibleSymbols[i]; j++) {
                const symbol_anim: SingleFireFrame = new SingleFireFrame(i, j);
                this._animItems[i].push(symbol_anim);
                this.addChild(symbol_anim);
            }
        }
        this.createEnhancerReelMeters();

        for (let i: number = 0; i < 2; i++) {
            const destPosX: number = this._enhancerMeterCountup[i].position.x;
            const destPosY: number = this._enhancerMeterCountup[i].position.y;
            const fireFrameLoop: TimelineSprite = MentalHelper.createSprite("fireFrameLoop", this, [destPosX, destPosY]);
            this._enhancerFireFrameLoop.push(fireFrameLoop);
            fireFrameLoop.angle = 90;
            fireFrameLoop.x += 12;
            fireFrameLoop.y += 8;
            fireFrameLoop.scale.set(1.3);
            fireFrameLoop.blendMode = PIXI.BLEND_MODES.ADD;
            fireFrameLoop.hide();
            fireFrameLoop.alpha = 1;
        }
    }

    private createEnhancerReelMeters(): void {
        for (let i: number = 0; i < 2; i++) {
            const ffCounter1: PIXI.Sprite = new PIXI.Sprite(Mental.assetLoader.getTextures("ffCounter1")[0]),
                meter: PIXI.BitmapText = new PIXI.BitmapText(i == 0 ? " /4" : " /6", {
                    fontName: "NumbersMultiplier",
                    fontSize: 16
                });

            ffCounter1.scale.set(0.3); // if we do the raw image as per this size, then it pixelates as the size comes out to be small.
            ffCounter1.anchor.set(0.5);

            meter.pivot.set(meter.width * 0.5, meter.height * 0.5);
            this.addChild(ffCounter1, meter);
            const posY: number = 198 * 3.155;
            if (i === 0) {
                ffCounter1.position.set(144 * 1.375, posY - 1);
                meter.position.set(144 * 1.5, posY);
            } else {
                ffCounter1.position.set(144 * 3.375, posY - 1);
                meter.position.set(144 * 3.5, posY);
            }
            this._ffCounter1Items.push(ffCounter1);
            this._enhancerMeterCountup.push(meter);
        }
    }

    private updateEnhancerMeters(): void {
        this._enhancerMeterCountup.forEach((value: PIXI.BitmapText, index: number) => {
            // #149
            this._ffCounter1Items[index].visible = false;

            // #107
            if ((index === 0 && !this._e1LighteningPlayed) || (index === 1 && !this._e2LighteningPlayed)) {
                const outOf: number = (index === 0 ? 4 : 6);
                let number: number = this._fireFrameCounter;
                if (number > 4 && index === 0) {
                    number = 4;
                } else if (number > 6 && index === 1) {
                    number = 6;
                }

                // #107
                if ((number + "") !== value.text[0]) {
                    value.text = number + "/" + outOf;
                }
            }
        });
    }

    private playLightningAnim(fireFramePosX: number, fireFramePosY: number, isFirst: boolean): void {
        const destPosX: number = this._enhancerMeterCountup[isFirst ? 0 : 1].position.x;
        const destPosY: number = this._enhancerMeterCountup[isFirst ? 0 : 1].position.y;
        const lightingEffect: TimelineSprite = MentalHelper.createSprite("lightingEffect", this, [fireFramePosX, fireFramePosY]);
        const boxOpenBlast: TimelineSprite = MentalHelper.createSprite("boxOpenBlast", this, [destPosX, destPosY]);
        const fireFrameLoop: TimelineSprite = isFirst ? this._enhancerFireFrameLoop[0] : this._enhancerFireFrameLoop[1];
        boxOpenBlast.scale.set(2);
        boxOpenBlast.y -= 20;
        boxOpenBlast.blendMode = PIXI.BLEND_MODES.ADD;
        lightingEffect.blendMode = PIXI.BLEND_MODES.ADD;
        const angle: number = MentalHelper.getAngleInDegree([fireFramePosX, fireFramePosY], [destPosX, destPosY - 45]);
        lightingEffect.anchor.set(0.5, 0);
        lightingEffect.angle = angle - 90;
        const distance: number = MentalHelper.getDistance(new PIXI.Point(fireFramePosX, fireFramePosY), new PIXI.Point(destPosX, destPosY));
        lightingEffect.scale.y = (distance + 20) / lightingEffect.height;

        const tl: TimelineLite = new TimelineLite();
        tl.add(TweenLite.to({}, 0.1, {}));
        tl.add(lightingEffect.getAnimationAutoShowHide(true, true));
        tl.add(boxOpenBlast.getAnimationAutoShowHide(true, true), 0.2);
        tl.add(() => {
            fireFrameLoop.show();
            fireFrameLoop.playLoop();
        }, 0.7);
        tl.add(() => {
            lightingEffect.parent.removeChild(lightingEffect);
            boxOpenBlast.parent.removeChild(boxOpenBlast);
        });
    }

    private removeFireFrame(): TimelineLite {
        const tl: TimelineLite = new TimelineLite();

        tl.to([this._enhancerFireFrameLoop[0], this._enhancerFireFrameLoop[1]], {duration: 0.2, alpha: 0});

        tl.add(() => {
            this._enhancerFireFrameLoop[0].stopLoop();
            this._enhancerFireFrameLoop[0].hide();
            this._enhancerFireFrameLoop[1].stopLoop();
            this._enhancerFireFrameLoop[1].hide();
        });
        return tl;
    }

    private getBoxOpenAnim(boxIndex: number): TimelineLite {
        const tl: TimelineLite = new TimelineLite();
        const destPosX: number = this._enhancerMeterCountup[boxIndex].position.x;
        const destPosY: number = this._enhancerMeterCountup[boxIndex].position.y;
        const door_smoke: TimelineSprite = MentalHelper.createSprite("door_smoke", this, [destPosX, destPosY + 55]);
        const doorOpenAnim: TimelineSprite = MentalHelper.createSprite("doorOpenAnim", this, [destPosX, destPosY + 30]);

        tl.add(doorOpenAnim.getAnimationAutoShowHide(true, false), 0);
        tl.add(door_smoke.getAnimationAutoShowHide(true, false), 0.2);
        tl.add([
            TweenLite.to(doorOpenAnim, 0.3, {alpha: 0}),
            TweenLite.to(door_smoke, 0.3, {alpha: 0})
        ])
        tl.add(() => {
            doorOpenAnim.parent.removeChild(doorOpenAnim);
            door_smoke.parent.removeChild(door_smoke);
        });
        return tl;
    }
}

export class SingleFireFrame extends PIXI.Container {
    public ffLockAnimation: PIXI.spine.Spine;
    private ffStart: TimelineSprite;
    private ffLoop: TimelineSprite;
    private _lockLandingAnim: string = "lock_landing_animation";
    private _lockLoop: string = "lock_loop_animation";

    constructor(reelId: number, symbolId: number) {
        super();
        this.initAnimation();
        this.position.set(((144) * (reelId + 1)) - 80, (198 * (symbolId + 1)) - 20);
        if (reelId === 1 || reelId === 3) {
            this.position.y += 198 / 2;
        }
    }

    public startAnimation(): void {
        this.ffStart.alpha = 1;
        this.ffStart.getAnimationAutoShowHide(true, true).add(() => {
            this.ffLoop.show();
            this.ffLoop.alpha = 1;
            this.ffLoop.playLoop();
        });
    }

    public stopAnimation(): void {
        TweenLite.to([this.ffStart, this.ffLoop, this.ffLockAnimation], {
            duration: 0.5, alpha: 0, onComplete: () => {
                this.ffLoop.stopLoop();
                this.ffLoop.hide();

                this.ffStart.hide();

                this.ffLockAnimation.state.setEmptyAnimation(0, 0);
                this.ffLockAnimation.state.clearTracks();
                this.ffLockAnimation.visible = false;
                this.ffLockAnimation.alpha = 1;
            }
        });
    }

    public playFFLandingLockAnimationInFS(): TimelineLite {
        if (this.ffLockAnimation.visible) {
            return new TimelineLite();
        }

        const tl: TimelineLite = new TimelineLite({
            onStart: () => {
                this.ffLockAnimation.visible = true;
                this.ffLockAnimation.alpha = 1;
            }
        });
        tl.add(() => {this.ffLockAnimation.state.setAnimation(0, this._lockLandingAnim, true)}, 0.001);

        return tl;
    }

    public playFFLockAnimation(isRestore: boolean = false): void {
        const alphaDuration: number = isRestore ? 0.001 : 0.5;
        this.ffLockAnimation.visible = true;
        this.ffLockAnimation.alpha = 0;

        TweenLite.to(this.ffLockAnimation, {alpha: 1, duration: alphaDuration});
        this.ffLockAnimation.state.setAnimation(0, this._lockLoop, true);
    }

    private initAnimation(): void {
        this.ffStart = new TimelineSprite(Mental.assetLoader.getTextures("fireFrameStart"));
        this.ffLoop = new TimelineSprite(Mental.assetLoader.getTextures("fireFrameLoop"));

        this.ffLockAnimation = new PIXI.spine.Spine(Mental.assetLoader.getSpineAsset("ffLock"));

        this.ffStart.blendMode = PIXI.BLEND_MODES.ADD;
        this.ffLoop.blendMode = PIXI.BLEND_MODES.ADD;

        this.ffStart.anchor.set(0.5);
        this.ffLoop.anchor.set(0.5);

        this.ffStart.scale.set(2);
        this.ffLoop.scale.set(2);

        this.ffStart.hide();
        this.ffLoop.hide();
        this.ffLockAnimation.visible = false;
        this.addChild(this.ffStart, this.ffLoop, this.ffLockAnimation);
    }
}